import React from 'react';
import { Modal, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'utils/dayjs';
import { TProspectInsurance } from 'types/common';
import { TBackendEligibilityCheckVo } from 'types/insuranceEligibility';
import { ELIGIBILITY_STATUS_CONFIG } from 'components/EligibilityStatusTag';
import { deriveEligibilityView, getPrimaryInsurances, getSecondaryInsurances } from 'utils/clientEligibility';
import { formatCheckedDate } from '../../../EligibilityModule/derive';
import s from './s.module.less';

type Props = {
    open: boolean;
    initialTab?: 'PRIMARY' | 'SECONDARY';
    onClose: () => void;
    onEdit: () => void;
    insurances: TProspectInsurance[];
    onViewImage?: (url: string) => void;
    // 每张卡最后一次 eligibility check（insuranceId → 摘要）：primary 卡底部报上次答案，
    // 让资料区和结果卡对得上（10/05）。不传则不渲染这一行
    checkCards?: Map<number, TBackendEligibilityCheckVo>;
};

const RELATIONSHIP_DISPLAY: Record<string, string> = {
    CHILD: "Client's child",
    PARENT: "Client's parent",
    SPOUSE: "Client's spouse",
    OTHER: "Client's other",
};

const renderPolicyHolder = (insurance: TProspectInsurance) => {
    if (insurance.policyHolderType === 'SOMEONE_ELSE' && insurance.relationship) {
        return RELATIONSHIP_DISPLAY[insurance.relationship] || `Client's ${insurance.relationship.toLowerCase()}`;
    }
    return 'Client';
};

const InsurancePopup = ({ open, initialTab = 'PRIMARY', onClose, onEdit, insurances, onViewImage, checkCards }: Props) => {
    const [activeTab, setActiveTab] = React.useState<'PRIMARY' | 'SECONDARY'>(initialTab);

    const primaryInsurances = React.useMemo(() => getPrimaryInsurances(insurances), [insurances]);
    const secondaryInsurances = React.useMemo(() => getSecondaryInsurances(insurances), [insurances]);

    const currentList = activeTab === 'PRIMARY' ? primaryInsurances : secondaryInsurances;

    React.useEffect(() => {
        if (open) {
            setActiveTab(initialTab);
        }
    }, [open, initialTab]);

    const lastCheckedText = (insurance: TProspectInsurance) => {
        const vo = insurance.id != null ? checkCards?.get(insurance.id) : undefined;
        if (!vo?.latestCheckedAt) return 'Never checked';
        const { label } = ELIGIBILITY_STATUS_CONFIG[deriveEligibilityView(vo, true).status];
        return `Last checked ${formatCheckedDate(vo.latestCheckedAt)}: ${label}`;
    };

    const renderExpandIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
                d="M2.33398 4.66634V2.33301M2.33398 2.33301H4.66732M2.33398 2.33301L5.25065 5.24967M11.6673 4.66634V2.33301M11.6673 2.33301H9.33398M11.6673 2.33301L8.75065 5.24967M2.33398 9.33301V11.6663M2.33398 11.6663H4.66732M2.33398 11.6663L5.25065 8.74967M11.6673 11.6663L8.75065 8.74967M11.6673 11.6663V9.33301M11.6673 11.6663H9.33398"
                stroke="#00816B"
                strokeWidth="1.16667"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const renderInsuranceCard = (insurance: TProspectInsurance, index: number) => (
        <div className={s.card} key={insurance.id || index}>
            <div className={s.row} style={{ marginTop: 0 }}>
                <div className={s.displayItem}>
                    <div className={s.label}>Primary policy holder</div>
                    <div className={s.value}>{renderPolicyHolder(insurance)}</div>
                </div>
            </div>
            <div className={s.row}>
                <div className={s.displayItem}>
                    <div className={s.label}>Member legal first name</div>
                    <div className={s.value}>
                        {(insurance.policyHolderType === 'SOMEONE_ELSE'
                            ? insurance.holderFirstName
                            : insurance.firstName) || '-'}
                    </div>
                </div>
                <div className={s.displayItem}>
                    <div className={s.label}>Member legal last name</div>
                    <div className={s.value}>
                        {(insurance.policyHolderType === 'SOMEONE_ELSE'
                            ? insurance.holderLastName
                            : insurance.lastName) || '-'}
                    </div>
                </div>
            </div>
            {insurance.policyHolderType === 'SOMEONE_ELSE' && (
                <div className={s.row}>
                    <div className={s.displayItem}>
                        <div className={s.label}>Member date of birth</div>
                        <div className={s.value}>
                            {insurance.holderDob
                                ? dayjs(insurance.holderDob, 'YYYY-MM-DD').format('MM/DD/YYYY')
                                : '-'}
                        </div>
                    </div>
                    <div className={s.displayItem}>
                        <div className={s.label}>Member sex</div>
                        <div className={s.value}>{insurance.holderGender || '-'}</div>
                    </div>
                </div>
            )}
            <div className={s.row}>
                <div className={s.displayItem}>
                    <div className={s.label}>Insurance carrier</div>
                    <div className={s.value}>{insurance.carrier || '-'}</div>
                </div>
                <div className={s.displayItem}>
                    <div className={s.label}>Insurance plan</div>
                    <div className={s.value}>{insurance.plan || '-'}</div>
                </div>
            </div>
            <div className={s.row}>
                <div className={s.displayItem}>
                    <div className={s.label}>Member ID</div>
                    <div className={s.value}>{insurance.memberId || '-'}</div>
                </div>
                <div className={s.displayItem}>
                    <div className={s.label}>Group ID</div>
                    <div className={s.value}>{insurance.groupId || '-'}</div>
                </div>
            </div>
            <div className={s.row}>
                <div className={s.displayItem}>
                    <div className={s.label}>Front of insurance card</div>
                    {insurance.front ? (
                        <div className={s.value}>
                            <img src={insurance.front} className={s.insurImg} />
                            <span
                                onClick={() => onViewImage?.(insurance.front!)}
                                className={s.viewImg}
                            >
                                {renderExpandIcon()}
                            </span>
                        </div>
                    ) : (
                        <div className={s.value}>-</div>
                    )}
                </div>
                <div className={s.displayItem}>
                    <div className={s.label}>Back of insurance card</div>
                    {insurance.backend ? (
                        <div className={s.value}>
                            <img src={insurance.backend} className={s.insurImg} />
                            <span
                                onClick={() => onViewImage?.(insurance.backend!)}
                                className={s.viewImg}
                            >
                                {renderExpandIcon()}
                            </span>
                        </div>
                    ) : (
                        <div className={s.value}>-</div>
                    )}
                </div>
            </div>
            {checkCards && activeTab === 'PRIMARY' && <div className={s.lastChecked}>{lastCheckedText(insurance)}</div>}
        </div>
    );

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={600}
            destroyOnClose
            closable={false}
            title={null}
            className={s.modalWrap}
        >
            <div className={s.headerWrap}>
                <span className={s.title}>Insurance</span>
                <div className={s.headerRight}>
                    <Button className={s.editBtn} onClick={onEdit}>Edit</Button>
                    <span className={s.closeBtn} onClick={onClose}>
                        <CloseOutlined />
                    </span>
                </div>
            </div>
            <div className={s.tabs}>
                <div
                    className={s.tab}
                    style={activeTab === 'PRIMARY' ? { color: '#fff', background: '#006957' } : undefined}
                    onClick={() => setActiveTab('PRIMARY')}
                >
                    Primary
                </div>
                <div
                    className={s.tab}
                    style={activeTab === 'SECONDARY' ? { color: '#fff', background: '#006957' } : undefined}
                    onClick={() => setActiveTab('SECONDARY')}
                >
                    Secondary
                </div>
            </div>
            <div className={s.cardList}>
                {currentList.length > 0 ? (
                    currentList.map((ins, idx) => renderInsuranceCard(ins, idx))
                ) : (
                    <div className={s.emptyState}>
                        No {activeTab.toLowerCase()} insurance
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default InsurancePopup;
